import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import Avatar from './Avatar';
import {
  Home,
  Users,
  UserCheck,
  MessageCircle,
  MapPin,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Heart,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  PlayCircle,
  Building,
  Stethoscope,
  Activity,
  Pill
} from 'lucide-react';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const location = useLocation();

  const navigationItems = [
    {
      name: 'Tableau de bord',
      href: '/dashboard',
      icon: Home,
      color: 'text-blue-600'
    },
    {
      name: 'Médecins',
      href: '/doctors',
      icon: UserCheck,
      color: 'text-green-600'
    },
    {
      name: 'Patients',
      href: '/patients',
      icon: Users,
      color: 'text-purple-600'
    },
    {
      name: 'Hôpitaux',
      href: '/hospitals',
      icon: Building,
      color: 'text-indigo-600'
    },
    {
      name: 'Consultations',
      href: '/consultations',
      icon: Stethoscope,
      color: 'text-cyan-600'
    },
    {
      name: 'Médicaments',
      href: '/medicaments',
      icon: Pill,
      color: 'text-green-600'
    },
    {
      name: 'Maladies',
      href: '/diseases',
      icon: AlertCircle,
      color: 'text-red-600'
    },
    {
      name: 'Études',
      href: '/studies',
      icon: FileText,
      color: 'text-orange-600'
    },
    {
      name: 'Zones de Santé',
      href: '/health-zones',
      icon: MapPin,
      color: 'text-teal-600'
    },
    {
      name: 'Bibliothèque',
      href: '/library',
      icon: BookOpen,
      color: 'text-blue-600'
    },
    {
      name: 'E-Learning',
      href: '/elearning',
      icon: PlayCircle,
      color: 'text-purple-600'
    },
    {
      name: 'Chat',
      href: '/chat',
      icon: MessageCircle,
      color: 'text-indigo-600'
    },
    {
      name: 'Carte',
      href: '/map',
      icon: MapPin,
      color: 'text-teal-600'
    },
    {
      name: 'Activités',
      href: '/activities',
      icon: Activity,
      color: 'text-yellow-600'
    }
  ];

  // Définir les menus visibles selon le rôle
  const role = (user?.role || '').toLowerCase();
  const allowedByRole = {
    // Admin voit tout
    admin: new Set(navigationItems.map((i) => i.name)),
    // Doctor
    doctor: new Set([
      'Tableau de bord',
      'Patients',
      'Consultations',
      'Médicaments',
      'Bibliothèque',
      'E-Learning',
      'Chat'
    ]),
    // Patient
    patient: new Set([
      'Tableau de bord',
      'Consultations',
      'Chat'
    ])
  };

  const visibleItems = (() => {
    const allowed = allowedByRole[role];
    if (!allowed) return navigationItems; // rôle inconnu → tout afficher par défaut
    if (role === 'admin') return navigationItems;
    return navigationItems.filter((item) => allowed.has(item.name));
  })();

  const handleLogout = () => {
    logout();
  };

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="bg-white p-2 rounded-lg shadow-lg border border-gray-200 hover-scale transition-all focus-ring"
        >
          {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50 overlay-enter"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 px-4 bg-gradient-to-r from-primary-600 to-primary-700">
            <div className="flex items-center space-x-2 hover-scale transition-all">
              <Heart className="h-8 w-8 text-white" />
              <span className="text-xl font-bold text-white">HealthBridge</span>
            </div>
          </div>

          {/* User Profile */}
          <div className="p-4 border-b border-gray-200">
            <Link 
              to="/profile" 
              onClick={() => setIsOpen(false)}
              className="flex items-center space-x-3 hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors duration-200"
            >
              <Avatar
                src={user?.avatar}
                alt={user?.name}
                name={user?.name}
                size="lg"
                isOnline={true}
                showOnlineStatus={true}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.role}
                </p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
            {visibleItems.map((item, index) => {
              const isActive = location.pathname === item.href;
              return (
                <div
                  key={item.name}
                  className={`animate-slide-in-left stagger-${Math.min(index + 1, 5)}`}
                >
                  <Link
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-500'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <item.icon className={`mr-3 h-5 w-5 ${isActive ? item.color : 'text-gray-400 group-hover:text-gray-500'}`} />
                    {item.name}
                  </Link>
                </div>
              );
            })}
          </nav>

          {/* Settings and Logout */}
          <div className="p-4 border-t border-gray-200 space-y-2">
            <button
              className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors duration-200 hover-scale-sm button-press"
            >
              <Settings className="mr-3 h-5 w-5" />
              Paramètres
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors duration-200 hover-scale-sm button-press"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:bg-white lg:shadow-xl lg:z-40 transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 px-4 bg-gradient-to-r from-primary-600 to-primary-700">
            <div className="flex items-center space-x-2 hover-scale transition-all">
              <Heart className="h-8 w-8 text-white" />
              {!isCollapsed && (
                <span className="text-xl font-bold text-white animate-fade-in">
                  HealthBridge
                </span>
              )}
            </div>
          </div>

          {/* Toggle Button */}
          <div className="flex justify-end p-2 border-b border-gray-200">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 hover-scale button-press focus-ring"
            >
              {isCollapsed ? (
                <ChevronRight className="h-5 w-5 text-gray-600" />
              ) : (
                <ChevronLeft className="h-5 w-5 text-gray-600" />
              )}
            </button>
          </div>

          {/* User Profile */}
          <div className="p-4 border-b border-gray-200">
            <Link 
              to="/profile"
              className={`flex items-center space-x-3 hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors duration-200 ${isCollapsed ? 'justify-center' : ''}`}
            >
              <Avatar
                src={user?.avatar}
                alt={user?.name}
                name={user?.name}
                size="lg"
                isOnline={true}
                showOnlineStatus={true}
              />
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.role}
                  </p>
                </div>
              )}
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
            {visibleItems.map((item, index) => {
              const isActive = location.pathname === item.href;
              return (
                <div
                  key={item.name}
                  className={`animate-slide-in-left stagger-${Math.min(index + 1, 5)}`}
                >
                  <Link
                    to={item.href}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-500'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    title={isCollapsed ? item.name : ''}
                  >
                    <item.icon className={`h-5 w-5 ${isCollapsed ? '' : 'mr-3'} ${isActive ? item.color : 'text-gray-400 group-hover:text-gray-500'}`} />
                    {!isCollapsed && (
                      <span className="animate-fade-in">
                        {item.name}
                      </span>
                    )}
                  </Link>
                </div>
              );
            })}
          </nav>

          {/* Settings and Logout */}
          <div className="p-4 border-t border-gray-200 space-y-2">
            <button
              className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors duration-200 hover-scale-sm button-press"
              title={isCollapsed ? 'Paramètres' : ''}
            >
              <Settings className={`h-5 w-5 ${isCollapsed ? '' : 'mr-3'}`} />
              {!isCollapsed && (
                <span className="animate-fade-in">
                  Paramètres
                </span>
              )}
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors duration-200 hover-scale-sm button-press"
              title={isCollapsed ? 'Déconnexion' : ''}
            >
              <LogOut className={`h-5 w-5 ${isCollapsed ? '' : 'mr-3'}`} />
              {!isCollapsed && (
                <span className="animate-fade-in">
                  Déconnexion
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;